"use client";

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import React, { createContext, type FC, type ReactNode, useContext, useState } from "react";

import { ChevronDownIcon } from "@/components/ui/primitives/icons";
import { cn } from "@/lib/utils";

/* ── Context ── */

interface DropDownContextProps {
  value?: string;
  onChange: (value: string) => void;
}

const DropDownContext = createContext<DropDownContextProps | undefined>(undefined);

const useDropDownContext = () => {
  const ctx = useContext(DropDownContext);
  if (!ctx) throw new Error("DropDown.Item must be used within DropDown");
  return ctx;
};

/* ── DropDownItem ── */

interface DropDownItemProps {
  value: string;
  children: ReactNode;
  className?: string;
}

const DropDownItem: FC<DropDownItemProps> = ({ value, children, className }) => {
  const { value: selectedValue, onChange } = useDropDownContext();
  const isActive = value === selectedValue;

  return (
    <DropdownMenuPrimitive.Item
      className={cn(
        "w-full h-10 px-3 py-3 rounded-4",
        "text-[15px] font-[600] leading-[140%] text-gray-900",
        "outline-none cursor-pointer",
        isActive ? "bg-primary-200 text-primary-900" : "bg-transparent",
        "focus:bg-gray-200 hover:bg-gray-200",
        className,
      )}
      onSelect={() => onChange(value)}
    >
      {children}
    </DropdownMenuPrimitive.Item>
  );
};
DropDownItem.displayName = "DropDownItem";

/* ── Helper: children에서 value에 맞는 label 추출 ── */

function getDisplayLabel(children: ReactNode, value?: string): string | undefined {
  if (!value) return undefined;
  let label: string | undefined;
  React.Children.forEach(children, (child) => {
    if (React.isValidElement<DropDownItemProps>(child) && child.props.value === value) {
      label = typeof child.props.children === "string" ? child.props.children : value;
    }
  });
  return label;
}

/* ── DropDown ── */

interface DropDownProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  w?: string;
  children: ReactNode;
  variant?: "default" | "outline";
  hasError?: boolean;
  maxVisibleItems?: number;
}

const DropDownBase: FC<DropDownProps> = ({
  value,
  onChange,
  placeholder = "Select option",
  w = "200px",
  children,
  variant = "default",
  hasError = false,
  maxVisibleItems,
}) => {
  const [open, setOpen] = useState(false);
  const displayLabel = getDisplayLabel(children, value);
  const isPlaceholder = !displayLabel;

  const triggerClass =
    variant === "outline"
      ? cn(
          "inline-flex items-center justify-between",
          "h-10 px-4 py-2 rounded-4",
          "text-[15px] font-[600] text-gray-900",
          "border-2 border-gray-300 bg-gray-300",
          "hover:bg-gray-700 hover:border-gray-700",
          "outline-none cursor-pointer",
        )
      : cn(
          "inline-flex items-center justify-between",
          "h-11 px-4 py-3 rounded-4",
          "text-[15px] text-gray-900",
          "border-2 bg-white",
          hasError ? "border-danger-400" : "border-gray-300",
          "outline-none cursor-pointer",
        );

  return (
    <DropDownContext.Provider value={{ value, onChange }}>
      <DropdownMenuPrimitive.Root open={open} onOpenChange={setOpen} modal={false}>
        <DropdownMenuPrimitive.Trigger className={triggerClass} style={{ width: w, minWidth: w }}>
          <span
            className={cn(
              "truncate text-left",
              isPlaceholder ? "font-[400] text-gray-700" : "font-[600] text-gray-900",
            )}
          >
            {isPlaceholder ? placeholder : displayLabel}
          </span>
          <ChevronDownIcon size={24} className={cn("shrink-0 transition-transform duration-200 text-gray-800", open && "rotate-180")} />
        </DropdownMenuPrimitive.Trigger>

        <DropdownMenuPrimitive.Portal>
          <DropdownMenuPrimitive.Content
            className={cn(
              "z-50 p-1 bg-white rounded-4 -mt-1",
              "shadow-[0px_0px_10px_5px_rgba(136,136,136,0.15)]",
            )}
            style={{
              minWidth: w,
              ...(maxVisibleItems ? { maxHeight: `${maxVisibleItems * 40}px`, overflowY: "auto" as const } : {}),
            }}
            sideOffset={4}
          >
            {children}
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      </DropdownMenuPrimitive.Root>
    </DropDownContext.Provider>
  );
};

/* ── Compound component assembly ── */

interface DropDownCompoundComponent extends FC<DropDownProps> {
  Item: typeof DropDownItem;
}

const DropDown = DropDownBase as DropDownCompoundComponent;
DropDown.Item = DropDownItem;

export { DropDown, DropDownItem };
export type { DropDownItemProps, DropDownProps };
